import { editorOptions } from "./defaults";
import type { IBlock, HE, IEditorOptions } from "../types";
import "../styles/main.scss";
import {
  frame,
  blocksContainer,
  // codeContainer,
  htmlContainer,
  cssContainer,
  // layers,
  toolbar,
  sectors,
  backIcon,
  topBar,
} from "../utils/editorLayoutElems";
import EventEmitter from "./eventEmitter";
import StyleManager from "./styleManager";
import BlockManager from "./blockManager";
import DeviceManager from "./deviceManager";
import ToolbarManager from "./toolbarManager";
import { $, $createEl, wrapper } from "../utils/domHelpers";
import { prepareElement } from "../utils/helpers";

class Editor {
  private editorContainer: HE;
  private config: IEditorOptions;
  private blocksContainer: HE;
  private styleManagerContainer: HE | null;
  private deviceManagerContainer: HE | null;
  private toolbarManagerContainer: HE;
  private selectedEl: HE | null;
  public selectedComponent: HE | null;
  public selectedBlock: IBlock | null;
  public htmlContainer: HTMLTextAreaElement;
  public cssContainer: HTMLTextAreaElement;
  private componentTree: [];
  private frame: HTMLIFrameElement | null;
  private page: HE | null;

  constructor(
    editorSelector: string,
    config: IEditorOptions = {},
    private StyleManager: StyleManager,
    private BlockManager: BlockManager,
    private DeviceManager: DeviceManager,
    private ToolbarManager: ToolbarManager,
    private ee: EventEmitter
  ) {
    this.editorContainer = $(editorSelector) as HE;
    this.blocksContainer = blocksContainer;
    this.styleManagerContainer = null;
    this.deviceManagerContainer = null;
    this.toolbarManagerContainer = toolbar as HE;
    this.htmlContainer = htmlContainer as HTMLTextAreaElement;
    this.cssContainer = cssContainer as HTMLTextAreaElement;
    this.config = { ...editorOptions, ...config };
    this.selectedEl = null;
    this.selectedComponent = null;
    this.selectedBlock = null;
    this.componentTree = [];
    this.frame = null;
    this.page = null;
    this.initializeEditor();
  }

  private initializeEditor() {
    this.loadEditor();
    this.initializeBlockManager();
    this.initializeToolbarManager();
    this.initializeStyleManager();
    this.initializeDeviceManager();
    this.initializeEventEmitter();

    console.log(
      "%cEditor%cv0.0.1",
      "font-size: 24px; color: cyan;",
      "font-size: 12px;"
    );
  }

  private initializeBlockManager() {
    if (this.blocksContainer) {
      this.BlockManager = new BlockManager(this.blocksContainer, this.ee);
      this.BlockManager.init();
    }
  }

  private initializeStyleManager() {
    if (this.styleManagerContainer) {
      this.StyleManager = new StyleManager(
        this.styleManagerContainer,
        this.ee,
        this
      );
      this.StyleManager.init();
    }
  }

  private initializeDeviceManager() {
    if (this.deviceManagerContainer) {
      this.DeviceManager = new DeviceManager(
        this.deviceManagerContainer as HE,
        this.ee,
        this
      );

      this.DeviceManager.init();
    }
  }

  private initializeToolbarManager() {
    if (this.toolbarManagerContainer) {
      this.ToolbarManager = new ToolbarManager(
        this.toolbarManagerContainer,
        this.ee,
        this
      );
      this.ToolbarManager.init();
    }
  }

  private initializeEventEmitter() {
    if (!this.ee) {
      this.ee = new EventEmitter();
    }
  }

  private loadEditor() {
    this.frame = frame as HTMLIFrameElement;

    backIcon.addEventListener("click", this.showBlockPanel);
    const sectorsContainer = wrapper(sectors, { id: "style-sectors" });
    this.styleManagerContainer = wrapper([backIcon, sectorsContainer], {
      id: "style-manager",
    });
    this.deviceManagerContainer = topBar.querySelector("#devices");

    this.editorContainer.append(
      wrapper([topBar, frame], { id: "editor-main" })
    );

    const style = $createEl(
      "style",
      `* {
            margin: 0;
        }

        body #page {
            height: 100%;
            margin: 0;
        }

        .mouseover {
            outline: 1px dashed #03a9f4 !important;
            outline-offset: -1px;
        }

        .selected {
            outline: 1px solid #03a9f4 !important;
            outline-offset: -1px;
        }

        #toolbar {
            position: absolute;
            top: 0;
            right: 0;
            display: none;
            gap: 0.5rem;
            padding: 3px 9px;
            background: #2196f3;
            color: white;
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;
          }

          #toolbar svg {
            cursor: pointer;
          }

          #toolbar .top {
            border-radius: 0;
            border-top-left-radius: 4px;
            border-top-right-radius: 4px;
          }`
    );

    if (this.frame.contentWindow?.document.head) {
      this.frame.contentWindow?.document.head.append(style);
    }

    const page = $createEl("div", "", {
      id: "page",
    });

    this.frame.contentDocument?.body.append(page);
    this.page = page as HTMLElement;

    prepareElement(this.page, this);

    const layoutElems = [
      this.blocksContainer,
      this.styleManagerContainer,
      // codeContainer,
      // layers,
    ];

    // @ts-ignore-next-line
    $("body").style.overflow = "hidden";
    this.editorContainer.append(wrapper(layoutElems, { id: "editor-sidebar" }));
  }

  addBlock(block: IBlock): void {
    this.BlockManager.add(block);
  }

  getBM(): BlockManager {
    return this.BlockManager;
  }

  getSM(): StyleManager {
    return this.StyleManager;
  }

  getDM(): DeviceManager {
    return this.DeviceManager;
  }

  getTM(): ToolbarManager {
    return this.ToolbarManager;
  }

  getConfig(): IEditorOptions {
    return this.config;
  }

  getDocument(): Document | undefined {
    return this.frame?.contentWindow?.document;
  }

  getFrame(): HE | null {
    return this.frame;
  }

  getPage(): HE | null {
    return this.page;
  }

  setSelectedEl(el: HE) {
    this.selectedEl = el;
  }

  getSelectedEl(): HE | null {
    return this.selectedEl;
  }

  showStylePanel() {
    this.BlockManager.hide();
    this.StyleManager.show();
  }

  getToolbarEl() {
    return this.toolbarManagerContainer;
  }

  getEditorEl() {
    return this.editorContainer;
  }

  showBlockPanel = () => {
    this.BlockManager.show();
    this.StyleManager.hide();
  };

  showToolbar() {
    this.ToolbarManager.show();
  }
}

export default Editor;
